(() => {
  if (customElements.get('collection-list-carousel')) return;

  class CollectionListCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-collection-carousel-track]');
      this.items = Array.from(this.querySelectorAll('[data-collection-carousel-item]'));
      this.previousButton = this.querySelector('[data-collection-carousel-previous]');
      this.nextButton = this.querySelector('[data-collection-carousel-next]');
      this.status = this.querySelector('[data-collection-carousel-status]');

      if (!this.track || this.items.length === 0 || !this.previousButton || !this.nextButton) return;

      this.isRtl = getComputedStyle(this.track).direction === 'rtl';
      this.prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.rafId = null;
      this.boundHandleWheel = (event) => this.onWheel(event);

      this.previousButton.addEventListener('click', () => this.move(-1));
      this.nextButton.addEventListener('click', () => this.move(1));
      this.track.addEventListener('scroll', () => this.requestUpdate(), { passive: true });
      this.track.addEventListener('keydown', (event) => this.onKeydown(event));
      this.track.addEventListener('wheel', this.boundHandleWheel, { passive: false });

      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(() => this.requestUpdate());
        this.resizeObserver.observe(this.track);
      } else {
        this.onResize = () => this.requestUpdate();
        window.addEventListener('resize', this.onResize, { passive: true });
      }

      this.items.forEach((item, index) => {
        item.setAttribute('aria-posinset', String(index + 1));
        item.setAttribute('aria-setsize', String(this.items.length));
      });

      this.requestUpdate();
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.onResize) window.removeEventListener('resize', this.onResize);
      if (this.track && this.boundHandleWheel) this.track.removeEventListener('wheel', this.boundHandleWheel);
      if (this.rafId) cancelAnimationFrame(this.rafId);
    }

    getPosition() {
      return this.isRtl ? Math.abs(this.track.scrollLeft) : this.track.scrollLeft;
    }

    getGap() {
      const styles = getComputedStyle(this.track);
      return parseFloat(styles.columnGap || styles.gap) || 0;
    }

    getScrollAmount() {
      const itemWidth = this.items[0].getBoundingClientRect().width;
      const itemStep = itemWidth + this.getGap();
      const visibleItems = Math.max(1, Math.floor((this.track.clientWidth + this.getGap()) / itemStep));
      return itemStep * visibleItems;
    }

    move(direction) {
      const physicalDirection = this.isRtl ? -direction : direction;
      this.track.scrollBy({
        left: physicalDirection * this.getScrollAmount(),
        behavior: this.prefersReducedMotion ? 'auto' : 'smooth'
      });
    }

    onKeydown(event) {
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      event.preventDefault();
      const direction = event.key === 'ArrowRight' ? 1 : -1;
      this.move(this.isRtl ? -direction : direction);
    }

    onWheel(event) {
      const verticalIntent = Math.abs(event.deltaY) > Math.abs(event.deltaX);
      if (!verticalIntent || event.ctrlKey || event.shiftKey) return;

      const deltaMultiplier = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      event.preventDefault();
      window.scrollBy(0, event.deltaY * deltaMultiplier);
    }

    requestUpdate() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = null;
        this.update();
      });
    }

    update() {
      const position = this.getPosition();
      const maximum = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
      this.previousButton.disabled = position <= 2;
      this.nextButton.disabled = maximum - position <= 2;

      if (!this.status) return;
      const trackRect = this.track.getBoundingClientRect();
      const visible = this.items
        .map((item, index) => ({ index, rect: item.getBoundingClientRect() }))
        .filter(({ rect }) => rect.right > trackRect.left + 1 && rect.left < trackRect.right - 1);

      if (visible.length === 0) return;
      const first = visible[0].index + 1;
      const last = visible[visible.length - 1].index + 1;
      this.status.textContent = `${first}–${last} / ${this.items.length}`;
    }
  }

  customElements.define('collection-list-carousel', CollectionListCarousel);
})();
